import type { LevelReading } from '../audio/api';
import type { AudioFilterDefinition, FilterProbeResult, SourceTypeDefinition, TransitionDefinition, VideoFilterDefinition, VideoFrameSource } from '../core/definitions';
import type { Id, Project, SettingsBag } from '../core/model';
import type { Logger, Unsubscribe } from '../core/services';
import type { DockContribution, RuntimeState, StudioApi, StudioEvents } from '../engine/api';
import {
  PLUGIN_API_VERSION,
  type PluginApi,
  type PluginHost,
  type PluginManifest,
  type PluginModule,
  type RemoteCommand,
} from './api';
import { createBuiltinHandlers, validateCommand, type RemoteHandler } from './remote';
import type { ProgramSourceEvidence } from '../render/api';

export interface PluginHostDeps {
  studio: StudioApi;
  getProgramStream(): MediaStream;
  getProgramSourceEvidence?(): ProgramSourceEvidence | null;
  getAudioContext(): AudioContext;
  probeTexture?(source: VideoFrameSource): boolean;
  probeFilterFragment?(fragment: string, key: string, originSampler?: boolean): FilterProbeResult;
  logger: Logger;
}

const PLUGIN_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*(?:\.[a-z0-9]+(?:-[a-z0-9]+)*)+$/;
const MAX_ID_LENGTH = 100;
const DEACTIVATE_TIMEOUT_MS = 5000;

type Entry = {
  manifest: PluginManifest;
  url: string;
  status: 'active' | 'error';
  error: string | null;
  /** Present while active: tears down everything the plugin registered. */
  teardown: (() => void) | null;
  module: PluginModule | null;
};

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function resolveUrl(url: string): string {
  if (typeof location === 'undefined') return url;
  try {
    return new URL(url, location.href).href;
  } catch {
    return url;
  }
}

function validateModule(mod: unknown): PluginModule {
  const def = (mod as { default?: unknown } | null)?.default;
  if (typeof def !== 'object' || def === null) throw new Error('Plugin module has no default export');
  const { manifest, activate, deactivate } = def as Partial<PluginModule>;
  if (typeof manifest !== 'object' || manifest === null) throw new Error('Plugin has no manifest');
  const m = manifest as Partial<PluginManifest>;
  if (typeof m.id !== 'string' || m.id.length > MAX_ID_LENGTH || !PLUGIN_ID_PATTERN.test(m.id)) {
    throw new Error('Plugin manifest id must be reverse-DNS style, e.g. "com.example.weather" (lowercase letters, digits, "-")');
  }
  if (typeof m.name !== 'string' || m.name.trim() === '') throw new Error('Plugin manifest needs a name');
  if (typeof m.version !== 'string' || m.version.trim() === '') throw new Error('Plugin manifest needs a version');
  if (m.apiVersion !== PLUGIN_API_VERSION) {
    throw new Error(`Plugin targets API version ${String(m.apiVersion)}, this app provides ${PLUGIN_API_VERSION}`);
  }
  if (m.description !== undefined && typeof m.description !== 'string') throw new Error('Plugin description must be a string');
  if (typeof activate !== 'function') throw new Error('Plugin has no activate() function');
  if (deactivate !== undefined && typeof deactivate !== 'function') throw new Error('Plugin deactivate must be a function');
  return def as PluginModule;
}

function copyManifest(m: PluginManifest): PluginManifest {
  return Object.freeze({
    id: m.id,
    name: m.name,
    version: m.version,
    apiVersion: m.apiVersion,
    ...(m.description !== undefined ? { description: m.description } : {}),
  });
}

export function createPluginHost(deps: PluginHostDeps): PluginHost {
  const { studio, logger } = deps;
  const log = (level: 'info' | 'warn' | 'error', scope: string, message: string, detail?: unknown) =>
    logger.log(level, scope, message, detail);

  const entries = new Map<string, Entry>();
  /** Plugins whose activate() has not settled; unload/destroy mark them cancelled instead of being no-ops. */
  const activations = new Map<string, { cancelled: boolean; settled: Promise<void> }>();
  const remoteHandlers = new Map<string, { pluginId: string; handler: RemoteHandler }>();
  const builtins = createBuiltinHandlers(studio);
  const listeners = new Set<() => void>();
  let destroyed = false;

  const notify = (): void => {
    for (const l of [...listeners]) {
      try {
        l();
      } catch (err) {
        log('error', 'plugins', `Plugin host listener threw: ${messageOf(err)}`);
      }
    }
  };

  function buildApi(manifest: PluginManifest): { api: PluginApi; teardown: () => void } {
    const pluginId = manifest.id;
    const scope = `plugin:${pluginId}`;
    const disposers = new Set<() => void>();
    let active = true;

    const assertActive = (): void => {
      if (!active) throw new Error(`Plugin "${pluginId}" is not active`);
    };

    const requirePrefixed = (value: string, what: string): void => {
      if (typeof value !== 'string' || !value.startsWith(`${pluginId}.`) || value.length === pluginId.length + 1) {
        throw new Error(`${what} "${String(value)}" must start with the plugin id "${pluginId}."`);
      }
    };

    /** Wraps a registration so it is torn down with the plugin, and idempotently on its own. */
    const track = (unsubscribe: Unsubscribe): Unsubscribe => {
      let done = false;
      const dispose = () => {
        if (done) return;
        done = true;
        disposers.delete(dispose);
        try {
          unsubscribe();
        } catch (err) {
          log('error', scope, `Cleanup failed: ${messageOf(err)}`);
        }
      };
      disposers.add(dispose);
      return dispose;
    };

    const guarded = <A extends unknown[]>(label: string, fn: (...args: A) => void) => (...args: A) => {
      if (!active) return;
      try {
        fn(...args);
      } catch (err) {
        log('error', scope, `${label} threw: ${messageOf(err)}`);
      }
    };

    const guard = <T extends object>(target: T, allow?: (prop: string | symbol, args: unknown[]) => void): T =>
      new Proxy(target, {
        get(obj, prop) {
          const value = Reflect.get(obj, prop, obj) as unknown;
          if (typeof value !== 'function') return value;
          return (...args: unknown[]) => {
            assertActive();
            allow?.(prop, args);
            return (value as (...a: unknown[]) => unknown).apply(obj, args);
          };
        },
      });

    const commands = guard(studio.commands, (prop, args) => {
      if (prop === 'setPluginData' && args[0] !== pluginId) {
        throw new Error(`Plugin "${pluginId}" may only write its own data`);
      }
    });
    const queries = guard(studio.queries);

    const api: PluginApi = {
      manifest,

      registerSource<S extends SettingsBag>(def: SourceTypeDefinition<S>) {
        assertActive();
        requirePrefixed(def.kind, 'Source kind');
        return track(studio.registry.registerSource(def));
      },
      registerVideoFilter<S extends SettingsBag>(def: VideoFilterDefinition<S>) {
        assertActive();
        requirePrefixed(def.kind, 'Filter kind');
        if (def.type !== 'video') throw new Error('registerVideoFilter needs a definition with type "video"');
        return track(studio.registry.registerFilter(def));
      },
      registerAudioFilter<S extends SettingsBag>(def: AudioFilterDefinition<S>) {
        assertActive();
        requirePrefixed(def.kind, 'Filter kind');
        if (def.type !== 'audio') throw new Error('registerAudioFilter needs a definition with type "audio"');
        return track(studio.registry.registerFilter(def));
      },
      registerTransition<S extends SettingsBag>(def: TransitionDefinition<S>) {
        assertActive();
        requirePrefixed(def.kind, 'Transition kind');
        return track(studio.registry.registerTransition(def));
      },
      registerDock(dock: DockContribution) {
        assertActive();
        requirePrefixed(dock.id, 'Dock id');
        return track(studio.registerDock(dock));
      },
      registerHotkeyAction(action) {
        assertActive();
        requirePrefixed(action.id, 'Hotkey action id');
        return track(
          studio.registerHotkeyAction({ id: action.id, label: action.label, run: guarded(`Hotkey action ${action.id}`, () => action.run()) }),
        );
      },
      registerRemoteHandler(type, handler) {
        assertActive();
        requirePrefixed(type, 'Remote command type');
        if (typeof handler !== 'function') throw new Error('Remote handler must be a function');
        if (remoteHandlers.has(type)) throw new Error(`Remote command "${type}" already has a handler`);
        const entry = { pluginId, handler };
        remoteHandlers.set(type, entry);
        return track(() => {
          if (remoteHandlers.get(type) === entry) remoteHandlers.delete(type);
        });
      },

      commands,
      queries,
      getProject: () => studio.project.getState().project,
      getRuntime: () => studio.runtime.getState(),
      onProjectChange(listener: (project: Project) => void) {
        assertActive();
        const safe = guarded('Project listener', listener);
        return track(
          studio.project.subscribe((state, prev) => {
            if (state.project !== prev.project) safe(state.project);
          }),
        );
      },
      onRuntimeChange(listener: (state: RuntimeState) => void) {
        assertActive();
        const safe = guarded('Runtime listener', listener);
        return track(studio.runtime.subscribe((state) => safe(state)));
      },
      on<K extends keyof StudioEvents>(event: K, listener: (payload: StudioEvents[K]) => void) {
        assertActive();
        return track(studio.on(event, guarded(`Listener for ${event}`, listener)));
      },
      selectScene(sceneId: Id) {
        assertActive();
        studio.selectScene(sceneId);
      },
      transition(toSceneId?: Id) {
        assertActive();
        return studio.transition(toSceneId);
      },
      async startRecording() {
        assertActive();
        await studio.startRecording();
      },
      async stopRecording() {
        assertActive();
        await studio.stopRecording();
      },
      startBroadcast() {
        assertActive();
        return studio.startBroadcast();
      },
      stopBroadcast() {
        assertActive();
        return studio.stopBroadcast();
      },
      getLevels: (): Readonly<Record<string, LevelReading>> => studio.getLevels(),

      getProgramStream() {
        assertActive();
        return deps.getProgramStream();
      },
      getProgramSourceEvidence() {
        assertActive();
        return deps.getProgramSourceEvidence?.() ?? null;
      },
      getAudioContext() {
        assertActive();
        return deps.getAudioContext();
      },
      probeTexture(source) {
        assertActive();
        return deps.probeTexture?.(source) ?? false;
      },
      probeFilterFragment(fragment, key, originSampler) {
        assertActive();
        return deps.probeFilterFragment?.(fragment, key, originSampler) ?? 'unavailable';
      },

      getData(): SettingsBag {
        const data = studio.project.getState().project.pluginData[pluginId];
        return data ? structuredClone(data) : {};
      },
      setData(data) {
        assertActive();
        studio.commands.setPluginData(pluginId, data);
      },

      notify(level, message) {
        assertActive();
        studio.notify(level, `${manifest.name}: ${message}`);
      },
    };

    const teardown = (): void => {
      active = false;
      for (const dispose of [...disposers].reverse()) dispose();
    };
    return { api, teardown };
  }

  function recordError(key: string, manifest: PluginManifest | null, url: string, error: string): void {
    entries.set(key, {
      manifest: manifest ?? { id: key, name: key, version: '', apiVersion: 0 },
      url,
      status: 'error',
      error,
      teardown: null,
      module: null,
    });
    notify();
  }

  async function load(url: string): Promise<PluginManifest> {
    if (destroyed) throw new Error('Plugin host is destroyed');
    if (typeof url !== 'string' || url.trim() === '') throw new Error('Plugin URL is required');
    const resolved = resolveUrl(url);
    for (const e of entries.values()) {
      if (e.status === 'active' && e.url === resolved) throw new Error(`Plugin at ${resolved} is already loaded`);
    }

    let mod: PluginModule;
    try {
      const imported: unknown = await import(/* @vite-ignore */ resolved);
      mod = validateModule(imported);
    } catch (err) {
      const message = `Could not load plugin ${resolved}: ${messageOf(err)}`;
      log('error', 'plugins', message);
      recordError(resolved, null, resolved, message);
      throw new Error(message);
    }

    const manifest = copyManifest(mod.manifest);
    const existing = entries.get(manifest.id);
    if ((existing && existing.status === 'active') || activations.has(manifest.id)) {
      throw new Error(`A plugin with id "${manifest.id}" is already loaded`);
    }
    // A failed attempt under the URL key is superseded now that the id is known.
    entries.delete(resolved);

    const { api, teardown } = buildApi(manifest);
    const activation = { cancelled: false, settled: Promise.resolve() };
    let markSettled!: () => void;
    activation.settled = new Promise<void>((resolve) => (markSettled = resolve));
    activations.set(manifest.id, activation);
    try {
      await mod.activate(api);
    } catch (err) {
      teardown();
      if (activation.cancelled || destroyed) throw new Error(`Plugin "${manifest.id}" was unloaded during activation`);
      const message = `Plugin "${manifest.id}" failed to activate: ${messageOf(err)}`;
      log('error', 'plugins', message);
      recordError(manifest.id, manifest, resolved, message);
      throw new Error(message);
    } finally {
      activations.delete(manifest.id);
      markSettled();
    }
    if (activation.cancelled || destroyed) {
      // Unloaded while activating: undo what it registered, and let it release what the host cannot track.
      try {
        await mod.deactivate?.();
      } catch (err) {
        log('error', `plugin:${manifest.id}`, `deactivate failed: ${messageOf(err)}`);
      }
      teardown();
      throw new Error(`Plugin "${manifest.id}" was unloaded during activation`);
    }

    entries.set(manifest.id, { manifest, url: resolved, status: 'active', error: null, teardown, module: mod });
    log('info', 'plugins', `Loaded plugin "${manifest.id}" ${manifest.version}`);
    notify();
    return manifest;
  }

  async function unloadEntry(id: string, entry: Entry): Promise<void> {
    entries.delete(id);
    try {
      if (entry.module?.deactivate) {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const timeout = new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error('deactivate() timed out')), DEACTIVATE_TIMEOUT_MS);
        });
        try {
          await Promise.race([Promise.resolve().then(() => entry.module!.deactivate!()), timeout]);
        } finally {
          clearTimeout(timer);
        }
      }
    } catch (err) {
      log('error', `plugin:${id}`, `deactivate failed: ${messageOf(err)}`);
    } finally {
      entry.teardown?.();
      notify();
    }
  }

  return {
    load,

    async unload(id) {
      const pending = activations.get(id);
      if (pending) {
        pending.cancelled = true;
        await pending.settled;
        return;
      }
      const entry = entries.get(id);
      if (entry) await unloadEntry(id, entry);
    },

    list() {
      return [...entries.values()].map((e) => ({ manifest: e.manifest, url: e.url, status: e.status, error: e.error }));
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },

    async dispatchRemote(command: RemoteCommand) {
      const { type, payload } = validateCommand(command);
      const builtin = Object.hasOwn(builtins, type) ? builtins[type] : undefined;
      if (builtin) return await builtin(payload);
      const registered = remoteHandlers.get(type);
      if (!registered) throw new Error(`Unknown remote command "${type}"`);
      try {
        return await registered.handler(payload);
      } catch (err) {
        log('error', `plugin:${registered.pluginId}`, `Remote handler "${type}" failed: ${messageOf(err)}`);
        throw err;
      }
    },

    async destroy() {
      if (destroyed) return;
      destroyed = true;
      const pending = [...activations.values()];
      for (const a of pending) a.cancelled = true;
      await Promise.all(pending.map((a) => a.settled));
      for (const [id, entry] of [...entries]) await unloadEntry(id, entry);
      listeners.clear();
    },
  };
}
